import { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "../../App";
import { styledToast as toast } from "@/components/ui/sonner";

export default function FarmerRegisterForm({ formData, setFormData, t }) {
  const [guardianType, setGuardianType] = useState("father");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.nameRequired');
    }
    
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (!phoneDigits) {
      newErrors.phone = t('validation.phoneRequired');
    } else if (phoneDigits.length !== 10) {
      newErrors.phone = t('validation.phoneInvalid');
    }
    
    if (!formData.guardianName.trim()) {
      newErrors.guardianName = t('validation.guardianRequired');
    }
    
    if (!formData.villageOfResidence.trim()) {
      newErrors.villageOfResidence = t('validation.villageRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 10);
    setFormData({ ...formData, phone: value });
    if (errors.phone) {
      setErrors({ ...errors, phone: null });
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error(t('validation.pleaseFixErrors'));
      return { success: false };
    }

    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        phone: formData.phone.replace(/\D/g, ''),
        role: "farmer",
        password: null,
        guardian_type: guardianType,
        guardian_name: formData.guardianName,
        village_of_residence: formData.villageOfResidence,
      };

      const response = await api.post("/auth/register", payload);
      return { 
        success: true, 
        token: response.data.access_token, 
        user: response.data.user 
      };
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMsg = detail.map(err => err.msg || err.message || JSON.stringify(err)).join(', ');
        toast.error(errorMsg);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(t('common.anErrorOccurred'));
      }
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  const getButtonText = () => {
    return loading ? t('common.processing') : t('register.createAccount');
  };

  return {
    render: (
      <>
        <div>
          <Label htmlFor="name">{t('register.fullName')} <span className="text-red-500">*</span></Label>
          <Input
            id="name"
            type="text"
            required
            value={formData.name}
            onChange={(e) => {
              setFormData({ ...formData, name: e.target.value });
              if (errors.name) setErrors({ ...errors, name: null });
            }}
            className={`mt-1.5 ${errors.name ? 'border-red-500' : ''}`}
            data-testid="input-name"
          />
          {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
        </div>

        <div>
          <Label>{t('register.guardianName')} <span className="text-red-500">*</span></Label>
          <div className="flex gap-2 mt-1.5">
            <Select value={guardianType} onValueChange={setGuardianType}>
              <SelectTrigger className="w-32" data-testid="guardian-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="father">{t('register.fatherOf')}</SelectItem>
                <SelectItem value="wife">{t('register.wifeOf')}</SelectItem>
              </SelectContent>
            </Select>
            <Input
              id="guardianName"
              type="text"
              required
              value={formData.guardianName}
              onChange={(e) => {
                setFormData({ ...formData, guardianName: e.target.value });
                if (errors.guardianName) setErrors({ ...errors, guardianName: null });
              }}
              className={`flex-1 ${errors.guardianName ? 'border-red-500' : ''}`}
              placeholder={guardianType === "father" ? t('register.fatherNamePlaceholder') : t('register.husbandNamePlaceholder')}
              data-testid="input-guardian-name"
            />
          </div>
          {errors.guardianName && <p className="text-red-500 text-xs mt-1">{errors.guardianName}</p>}
        </div>

        <div>
          <Label htmlFor="villageOfResidence">{t('register.villageOfResidence')} <span className="text-red-500">*</span></Label>
          <Input
            id="villageOfResidence"
            type="text"
            required
            value={formData.villageOfResidence}
            onChange={(e) => {
              setFormData({ ...formData, villageOfResidence: e.target.value });
              if (errors.villageOfResidence) setErrors({ ...errors, villageOfResidence: null });
            }}
            className={`mt-1.5 ${errors.villageOfResidence ? 'border-red-500' : ''}`}
            placeholder={t('register.villageOfResidencePlaceholder')}
            data-testid="input-village-residence"
          />
          {errors.villageOfResidence && <p className="text-red-500 text-xs mt-1">{errors.villageOfResidence}</p>}
        </div>

        <div>
          <Label htmlFor="phone">{t('register.phoneRequired')} <span className="text-red-500">*</span></Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={handlePhoneChange}
            className={`mt-1.5 ${errors.phone ? 'border-red-500' : ''}`}
            placeholder="9876543210"
            maxLength={10}
            data-testid="input-phone"
          />
          {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
          <p className="text-slate-500 text-xs mt-1">{t('register.phoneHint')}</p>
        </div>
      </>
    ),
    handleSubmit,
    getButtonText,
    loading
  };
}
